import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HoverCard from './HoverCard';
import { plainText, firstSentence } from './ConditionCardLayer';
import formatScalar from './formatValue';
import { getFamily } from '../../config/familyRegistry';
import { getActiveSystem } from '../../config/system';
import { getPreview, peekPreview, warmPreviews } from '../../utils/entryPreviews';
import { getHoverFields, isCompact, DEFAULT_CARD_ROWS } from '../../config/hoverFields';
import { MAX_HOVER_FIELDS } from '../../config/preferencesSync';
import { track } from '../../utils/analytics';

/**
 * The site's hover card for entity links (issue 73) — mounted ONCE in Layout.
 *
 * Every link the generic engine renders to an entry carries
 * `data-preview="<segment>/<slug>"` (AttrValue's `LinkOrText`, the list card,
 * the similar rail), and this layer listens for them on the document. Resting
 * the pointer on one, or focusing it from the keyboard, raises a card with the
 * entry's name and `family.detail.quickStats` — the SAME set of scalars the
 * detail page shows, from the registry rather than a per-Family list, so a
 * user-authored Family gets a card because its declaration says what a reader
 * sees and the card cannot contradict the page it previews.
 *
 * What keeps this from costing more than it saves (the issue's own warning):
 *
 *   · HOVER INTENT — no request until the pointer has rested INTENT_MS. Most
 *     hovers are a cursor crossing a link on its way somewhere else.
 *   · A SESSION CACHE keyed by segment and slug (`utils/entryPreviews.js`).
 *   · BATCHING BY SURFACE — the first intent on a page asks for every
 *     `a[data-preview]` of that segment on the page in one request, so a list
 *     of 20 rows, a similar rail or a detail page's reference links cost one
 *     round trip, and only once a reader has shown they hover at all.
 *
 * TOUCH RAISES NO CARD. Tapping a link navigates, exactly as before: the card
 * is a pointer-and-keyboard affordance, decided on the issue.
 *
 * ONE DELEGATED CARD, NOT A TOOLTIP PER LINK — the #36 shape. Nothing is
 * injected beside the links, so the prose's own text content and every link's
 * accessible name are exactly what they were. Escape dismisses without moving
 * focus; leaving, blurring, scrolling and navigating dismiss too.
 */
export const INTENT_MS = 150;
const CARD_WIDTH = 384; // matches the CSS max-width of 24rem

const SELECTOR = 'a[data-preview]';

function parseTarget(el) {
  const raw = el.getAttribute('data-preview') || '';
  const at = raw.indexOf('/');
  if (at <= 0) return null;
  return { segment: raw.slice(0, at), slug: raw.slice(at + 1) };
}

/**
 * The rows a card shows. The reader's own choice for this family (issue 376,
 * `hoverFields[system][family]`, in the order they picked) when they made
 * one; otherwise the detail's quick stats — the registry-derived default #73
 * shipped. A chosen key the family no longer declares, or that is not a
 * compact value, is simply absent, the way every stored address degrades.
 */
function cardAttributes(family, system) {
  const chosen = getHoverFields(system, family.family);
  const attrs = chosen
    ? chosen.map((key) => family.attributes.find((a) => a.key === key)).filter((a) => a && isCompact(a))
    : family.detail.quickStats;
  return { attrs, chosen };
}

/**
 * Whether a card for `segment` should carry a summary sentence (issue 1017):
 * when the family has no rows to show — a glossary term is a name and its
 * definition, and a card of the name alone tells the reader nothing the link
 * did not. Registry-driven: no family is named.
 */
export function wantsSummary(segment) {
  const system = getActiveSystem();
  const family = getFamily(segment, system);
  if (!family) return false;
  return cardAttributes(family, system).attrs.length === 0;
}

export function cardRows(segment, values) {
  const system = getActiveSystem();
  const family = getFamily(segment, system);
  if (!family || !values) return [];
  const { attrs, chosen } = cardAttributes(family, system);
  const cap = chosen ? MAX_HOVER_FIELDS : DEFAULT_CARD_ROWS;
  const rows = [];
  for (const attr of attrs) {
    const value = values[attr.key];
    if (value === undefined || value === null || value === '') continue;
    const text = Array.isArray(value) ? value.map((v) => formatScalar(attr, v)).join(', ') : formatScalar(attr, value);
    if (!text) continue;
    rows.push({ label: attr.label, text });
    if (rows.length >= cap) break;
  }
  return rows;
}

/** Every slug of `segment` linked on the page, so one request warms them all. */
function slugsOnPage(segment) {
  const out = [];
  document.querySelectorAll(SELECTOR).forEach((el) => {
    const t = parseTarget(el);
    if (t && t.segment === segment) out.push(t.slug);
  });
  return out;
}

function placement(el) {
  // A list card is one big link; anchor its card under the title rather than
  // under the whole card, which would land it over the next row.
  const anchor = el.querySelector('h2') || el;
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth || 1024;
  const vh = window.innerHeight || 768;
  const left = Math.max(8, Math.min(r.left, vw - CARD_WIDTH - 8));
  // Below the link when there is room, above it when the link sits low.
  if (r.bottom + 220 < vh || r.top < vh / 2) return { position: 'fixed', top: r.bottom + 6, left };
  return { position: 'fixed', bottom: vh - r.top + 6, left };
}

export default function HoverCardLayer() {
  const location = useLocation();
  const [tip, setTip] = useState(null);
  const timer = useRef(null);
  const active = useRef(null); // the element the pending or shown card is for
  // Where the pointer last was. A page that MOVES under a resting pointer
  // (the session read resolving inserts the signed-in header and the
  // handle note above the content) fires pointerout on the link with the
  // pointer's coordinates unchanged — the reader has not left anything.
  // Issue 638: that pointerout cancelled every card raised before the
  // session hydrated. A pointerout at the last known position is a layout
  // shift, and the card is kept while the link still contains the point.
  const pointer = useRef(null);
  // The entries whose card was counted on this page view: hovering one again does not count again (issue 1600).
  const counted = useRef(new Set());
  useEffect(() => { counted.current = new Set(); }, [location.pathname, location.search]);

  const hide = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    active.current = null;
    setTip(null);
  }, []);

  const raise = useCallback((el) => {
    const target = parseTarget(el);
    if (!target) return;
    active.current = el;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      if (active.current !== el) return;
      const known = peekPreview(target.segment, target.slug);
      const opts = { summary: wantsSummary(target.segment) };
      const ready = known !== undefined
        ? Promise.resolve(known)
        : warmPreviews(target.segment, slugsOnPage(target.segment), opts)
          .then(() => getPreview(target.segment, target.slug, opts));
      ready.then((preview) => {
        if (active.current !== el || !preview) return;
        const seen = `${target.segment}/${target.slug}`;
        const fam = getFamily(target.segment, getActiveSystem());
        if (fam && !counted.current.has(seen)) { counted.current.add(seen); track('hover-card', { family: fam.family }); }
        setTip({
          name: preview.name,
          text: preview.summary ? firstSentence(plainText(preview.summary)) : '',
          rows: cardRows(target.segment, preview.values),
          style: placement(el),
        });
      });
    }, INTENT_MS);
  }, []);

  useEffect(() => {
    const over = (e) => {
      if (e.pointerType === 'touch') return;
      const el = e.target && e.target.closest && e.target.closest(SELECTOR);
      if (!el) return;
      if (el !== active.current) raise(el);
    };
    const out = (e) => {
      const el = e.target && e.target.closest && e.target.closest(SELECTOR);
      if (!el || el !== active.current) return;
      // Moving between two children of the same link is not leaving it.
      const to = e.relatedTarget;
      if (to && to.nodeType === 1 && el.contains(to)) return;
      if (isLayoutShift(e, el)) return;
      hide();
    };
    const moved = (e) => { pointer.current = { x: e.clientX, y: e.clientY }; };
    // A pointerout whose coordinates are the pointer's last known position
    // was not caused by the pointer: the page moved. Keep the card while the
    // link still contains that point; a link that moved out from under the
    // pointer is left, and the card hides as before.
    const isLayoutShift = (e, el) => {
      const last = pointer.current;
      if (!last || e.clientX !== last.x || e.clientY !== last.y) return false;
      if (typeof document.elementFromPoint !== 'function') return true;
      const under = document.elementFromPoint(last.x, last.y);
      return !!(under && el.contains(under));
    };
    const focus = (e) => {
      const el = e.target && e.target.closest && e.target.closest(SELECTOR);
      if (el && el !== active.current) raise(el);
    };
    const blur = (e) => {
      const el = e.target && e.target.closest && e.target.closest(SELECTOR);
      if (el && el === active.current) hide();
    };
    const key = (e) => { if (e.key === 'Escape') hide(); };
    // A shown card is fixed to the viewport, so scrolling takes it off its
    // link: hide it. A card still PENDING is a different case — hovering a
    // link below the fold scrolls it into view first (smoothly, so the scroll
    // events outlast the intent timer), and cancelling there meant no link
    // below the fold ever raised a card. The intent simply restarts.
    const scrolled = () => {
      if (timer.current && active.current) raise(active.current);
      else hide();
    };
    document.addEventListener('pointermove', moved, { passive: true });
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', blur);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', scrolled, true);
    return () => {
      document.removeEventListener('pointermove', moved);
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', blur);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', scrolled, true);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [raise, hide]);

  // Navigating away takes the link with it.
  useEffect(() => { hide(); }, [location.pathname, location.search, hide]);

  if (!tip) return null;
  return (
    <HoverCard
      className="entry-card"
      name={tip.name}
      text={tip.text}
      rows={tip.rows}
      style={tip.style}
    />
  );
}
